import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store, ofActionDispatched } from '@ngxs/store';
import _ from 'lodash';
import { EMPTY } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil } from 'rxjs/operators';
import { UpdateRegistrationOfContactStates } from 'src/app/store/samwin/contact-states/contact-states.actions';
import { DirectoriesService, DirectoryEntryRemoteDto } from 'src/generated/swagger';
import {
   DirectoryValuesLoaded,
   DirectoryValuesRemoveEntry,
   DirectoryValuesUpdateEntry,
   QueryDirectory,
   SetAllDataLoaded,
   SetLoadedDirectoryValues,
   SetSearchExecuted,
   SetSearchRelatedData,
} from './directory-data-behaviour.actions';

export interface DirectoryDataBehaviourModel {
   [brickInstanceId: string]: DirectoryDataBehaviourInstance;
}

export interface DirectoryDataBehaviourInstance {
   loadedDirectoryValues: DirectoryEntryRemoteDto[];
   wasSearchExecuted: boolean;
   isAllDataLoaded: boolean;
}

export function defaultDirectoryDataBehaviourInstance(): DirectoryDataBehaviourInstance {
   return {
      loadedDirectoryValues: [],
      wasSearchExecuted: false,
      isAllDataLoaded: false,
   };
}

@State<DirectoryDataBehaviourModel>({
   name: 'directoryDataBehaviour',
   defaults: {},
})
@Injectable()
export class DirectoryDataBehaviourState {
   constructor(private directoriesService: DirectoriesService, private actions$: Actions, private store: Store) {}

   @Selector([DirectoryDataBehaviourState])
   public static getSpecificBehaviourStateOrDefault(state: DirectoryDataBehaviourModel) {
      return (brickInstanceId: string) => {
         return DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
      };
   }

   @Selector([DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getDirectoryEntries(directoryBrowserBehaviourForBrick: (brickInstanceId: string) => DirectoryDataBehaviourInstance) {
      return (brickInstanceId: string) => {
         return directoryBrowserBehaviourForBrick(brickInstanceId).loadedDirectoryValues ?? [];
      };
   }

   @Selector([DirectoryDataBehaviourState.getDirectoryEntries])
   public static getDirectoryEntryById(getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[]) {
      return (brickInstanceId: string, id: number) => {
         return getDirectoryEntries(brickInstanceId).find((entry) => entry.id === id);
      };
   }

   @Selector([DirectoryDataBehaviourState.getDirectoryEntries])
   public static getFirstEntry(getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[]) {
      return (brickInstanceId: string) => {
         return _.head(getDirectoryEntries(brickInstanceId));
      };
   }

   @Selector([DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefault])
   public static wasSearchExecuted(directoryBrowserBehaviourForBrick: (brickInstanceId: string) => DirectoryDataBehaviourInstance) {
      return (brickInstanceId: string) => {
         return directoryBrowserBehaviourForBrick(brickInstanceId).wasSearchExecuted;
      };
   }

   @Selector([DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getIsAllDataLoaded(specificBehaviourStateFn: (brickInstanceId: string) => DirectoryDataBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourStateFn(brickInstanceId).isAllDataLoaded;
      };
   }

   private static getSpecificBehaviourStateOrDefaultInternal(state: DirectoryDataBehaviourModel, brickInstanceId: string): DirectoryDataBehaviourInstance {
      const value = state[brickInstanceId];
      if (value) {
         return value;
      } else {
         return defaultDirectoryDataBehaviourInstance();
      }
   }

   @Action(DirectoryValuesLoaded)
   @ImmutableContext()
   public directoryValuesLoaded(ctx: StateContext<DirectoryDataBehaviourModel>, action: DirectoryValuesLoaded) {
      const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId));
      const currentEntries = _.cloneDeep(directoryState?.loadedDirectoryValues ?? []);
      const updatedEntries = _.concat(currentEntries, action.resultEntries);
      ctx.dispatch(new SetLoadedDirectoryValues(action.brickInstanceId, updatedEntries));
   }

   @Action(SetLoadedDirectoryValues)
   @ImmutableContext()
   public setLoadedDirectoryValues(ctx: StateContext<DirectoryDataBehaviourModel>, action: SetLoadedDirectoryValues) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.loadedDirectoryValues = action.loadedDirectoryValues;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetSearchExecuted)
   @ImmutableContext()
   public setSearchExecuted(ctx: StateContext<DirectoryDataBehaviourModel>, action: SetSearchExecuted) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.wasSearchExecuted = action.wasSearchExecuted;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetAllDataLoaded)
   @ImmutableContext()
   public setAllDataLoaded(ctx: StateContext<DirectoryDataBehaviourModel>, action: SetAllDataLoaded) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.isAllDataLoaded = action.allDataLoaded;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(SetSearchRelatedData)
   @ImmutableContext()
   public setSearchRelatedData(ctx: StateContext<DirectoryDataBehaviourModel>, action: SetSearchRelatedData) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         directoryState.loadedDirectoryValues = action.loadedDirectoryValues;
         directoryState.wasSearchExecuted = action.wasSearchExecuted;
         directoryState.isAllDataLoaded = action.allDataLoaded;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(DirectoryValuesUpdateEntry)
   @ImmutableContext()
   public directoryValuesUpdateEntry(ctx: StateContext<DirectoryDataBehaviourModel>, action: DirectoryValuesUpdateEntry) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = _.cloneDeep(DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId));
         const currentIndex = _(directoryState.loadedDirectoryValues).findIndex((value) => value.id === action.id);
         if (currentIndex === -1) {
            return state;
         }
         directoryState.loadedDirectoryValues[currentIndex] = action.entry;
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(DirectoryValuesRemoveEntry)
   @ImmutableContext()
   public directoryValuesRemoveEntry(ctx: StateContext<DirectoryDataBehaviourModel>, action: DirectoryValuesRemoveEntry) {
      ctx.setState((state: DirectoryDataBehaviourModel) => {
         const directoryState = DirectoryDataBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         directoryState.loadedDirectoryValues = _.filter(directoryState.loadedDirectoryValues, (x) => x.id !== action.id);
         state[action.brickInstanceId] = directoryState;
         return state;
      });
   }

   @Action(QueryDirectory)
   @ImmutableContext()
   public queryDirectory(ctx: StateContext<DirectoryDataBehaviourModel>, action: QueryDirectory) {
      return this.directoriesService
         .queryDirectory({
            searchFilter: action.searchFilter,
            pagingInfo: {
               take: action.pagingInfo.take,
               skip: action.pagingInfo.skip,
            },
            sortByColumnKey: action.sortByColumnKey,
            sortByDescending: action.sortByDescending,
            searchableColumns: action.searchableColumns,
            limitToDirectoryEntries: action.limitToDirectoryEntries,
            restrictToSubscriberId: action.restrictToSubscriberId,
            matchingPercentage: action.matchingPercentage,
         })
         .pipe(
            mergeMap((x) => {
               const allDataLoaded = x.resultEntries.length < action.pagingInfo.take;

               const currentEntries = this.store.selectSnapshot(DirectoryDataBehaviourState.getDirectoryEntries)(action.brickInstanceId);
               const updatedEntries = _.concat(currentEntries, x.resultEntries);

               return ctx.dispatch(new SetSearchRelatedData(action.brickInstanceId, updatedEntries, true, allDataLoaded));
            }),
            mergeMap(() => ctx.dispatch(new UpdateRegistrationOfContactStates())),
            catchError((error) => {
               console.error('Error during query directory', error);
               return EMPTY;
            }),
            takeUntil(
               this.actions$.pipe(
                  ofActionDispatched(QueryDirectory),
                  filter((x: QueryDirectory) => x.brickInstanceId === action.brickInstanceId)
               )
            )
         );
   }
}
